import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import PengaduanAdminPanel from "layouts/pages/admin/pengaduan/components/PengaduanAdminPanel";
import api from "services/api";
import { requireAuth, getCurrentUser } from "actions/AuthAction";

const quickLinks = [
  { title: "Jenis Izin", desc: "Kelola master layanan perizinan", to: "/admin/jenis-izin", icon: "description", color: "info", superOnly: false },
  { title: "Pengguna Staff", desc: "Kelola akun admin, petugas, verifikator, pimpinan", to: "/admin/users", icon: "group", color: "success", superOnly: true },
  { title: "Daftar Pengajuan", desc: "Lihat semua permohonan perizinan", to: "/pengajuan", icon: "assignment", color: "dark", superOnly: false },
  { title: "Antrian Petugas", desc: "Proses verifikasi pengajuan", to: "/dashboard/petugas", icon: "fact_check", color: "warning", superOnly: false },
  { title: "Galeri Aktivitas", desc: "Kelola foto kegiatan di landing page", to: "/admin/galeri-aktivitas", icon: "photo_library", color: "primary", superOnly: false },
  { title: "Aset Gambar", desc: "Kelola logo dan background sistem", to: "/admin/aset-gambar", icon: "image", color: "info", superOnly: false },
];

const pengajuanStatCards = [
  { key: "total", label: "Total Pengajuan", icon: "assignment", color: "dark", fromSummary: true },
  { key: "DIAJUKAN", label: "Diajukan", icon: "inbox", color: "info" },
  { key: "VERIFIKASI", label: "Diverifikasi", icon: "fact_check", color: "warning" },
  { key: "PERBAIKAN", label: "Perlu Perbaikan", icon: "build", color: "error" },
  { key: "PROSES_TEKNIS", label: "Proses Teknis", icon: "engineering", color: "warning" },
  { key: "DISETUJUI", label: "Disetujui", icon: "thumb_up", color: "success" },
  { key: "PENANDATANGANAN", label: "Penandatanganan", icon: "draw", color: "primary" },
  { key: "SELESAI", label: "Selesai", icon: "check_circle", color: "success" },
  { key: "DITOLAK", label: "Ditolak", icon: "cancel", color: "error" },
  { key: "DRAFT", label: "Draft", icon: "edit", color: "secondary" },
];

const emptyStats = () => ({
  total: 0,
  total_aktif: 0,
  per_status: {
    DRAFT: 0,
    DIAJUKAN: 0,
    VERIFIKASI: 0,
    PERBAIKAN: 0,
    PROSES_TEKNIS: 0,
    DISETUJUI: 0,
    PENANDATANGANAN: 0,
    SELESAI: 0,
    DITOLAK: 0,
  },
});

const buildStatsFromPengajuan = (rows = []) => {
  const perStatus = { ...emptyStats().per_status };

  rows.forEach((row) => {
    const status = row.status || "DRAFT";
    perStatus[status] = (perStatus[status] || 0) + 1;
  });

  return {
    total: rows.length,
    total_aktif: rows.filter((row) => row.status !== "DRAFT").length,
    per_status: perStatus,
  };
};

function DashboardAdmin() {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const visibleLinks = quickLinks.filter((item) => !item.superOnly || user?.role === "SUPER_ADMIN");
  const [loadingStats, setLoadingStats] = useState(true);
  const [stats, setStats] = useState(() => emptyStats());
  const dashboardTitle = user?.role === "SUPER_ADMIN" ? "Dashboard Super Admin" : "Dashboard Admin";

  useEffect(() => {
    if (!requireAuth(navigate, ["ADMIN", "SUPER_ADMIN"])) return undefined;

    setLoadingStats(true);
    api
      .get("pengajuan")
      .then((res) => setStats(buildStatsFromPengajuan(res.data?.data || [])))
      .catch(() => setStats(emptyStats()))
      .finally(() => setLoadingStats(false));

    return undefined;
  }, [navigate]);

  const getStatCount = (item) => {
    if (loadingStats) return "...";
    if (item.fromSummary) return stats.total ?? 0;
    return stats.per_status?.[item.key] ?? 0;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="medium">
            {dashboardTitle}
          </MDTypography>
          <MDTypography variant="button" color="text">
            DPMPTSP Kabupaten Rote Ndao — panel administrasi TIALAI
          </MDTypography>
        </MDBox>

        <MDBox mb={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h6" fontWeight="medium">
              Statistik Pengajuan
            </MDTypography>
            <MDButton variant="outlined" color="info" component={Link} to="/pengajuan" size="small">
              Lihat Daftar Pengajuan
            </MDButton>
          </MDBox>
          <Grid container spacing={3}>
            {pengajuanStatCards.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.key}>
                <MiniStatisticsCard
                  title={{ text: item.label, fontWeight: "medium" }}
                  count={getStatCount(item)}
                  icon={{ color: item.color, component: item.icon }}
                  direction="right"
                />
              </Grid>
            ))}
          </Grid>
        </MDBox>

        <Grid container spacing={3} mb={4}>
          {visibleLinks.map((item) => (
            <Grid item xs={12} md={6} lg={3} key={item.title}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={3} display="flex" flexDirection="column" height="100%">
                  <MDBox
                    variant="gradient"
                    bgColor={item.color}
                    color="white"
                    width="3rem"
                    height="3rem"
                    borderRadius="md"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <Icon fontSize="small">{item.icon}</Icon>
                  </MDBox>
                  <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                    {item.title}
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2} flex={1}>
                    {item.desc}
                  </MDTypography>
                  <MDButton variant="outlined" color={item.color} component={Link} to={item.to} size="small">
                    Buka
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>

        <PengaduanAdminPanel compact title="Pengaduan & Masukan Terbaru" />

        <MDBox mt={2} textAlign="right">
          <MDButton variant="gradient" color="info" component={Link} to="/admin/pengaduan">
            Kelola Semua Pengaduan
          </MDButton>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default DashboardAdmin;
